// The Fleetly Platform Manual's markdown, parsed into blocks the Manual page
// renders as React elements. Plain .ts so Node's test runner can check it
// (web/test/manualMarkdown.test.ts).
//
// THE SUBSET IS FIXED, and that is the point: #, ##, ###, paragraphs, "- "
// bullets, "1. " lists, **bold**, pipe tables and "> " callouts. That is every
// construct the v1.0 manual uses (counted on 5 October 2026: no code, no links,
// no HTML, no nesting). Anything else is shown as the literal text it is, never
// interpreted, and nothing is ever handed to dangerouslySetInnerHTML, so text
// in the manual can never become markup in the cockpit.

export type Inline = { text: string; bold: boolean }

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3; text: Inline[] }
  | { kind: 'paragraph'; text: Inline[] }
  | { kind: 'bullets'; items: Inline[][] }
  | { kind: 'numbered'; start: number; items: Inline[][] }
  | { kind: 'table'; header: Inline[][]; rows: Inline[][][] }
  | { kind: 'callout'; text: Inline[] }

/** Splits on **bold** pairs. An unpaired ** stays literal. */
export function parseInline(s: string): Inline[] {
  const out: Inline[] = []
  let rest = s
  for (;;) {
    const a = rest.indexOf('**')
    if (a < 0) break
    const b = rest.indexOf('**', a + 2)
    if (b < 0) break
    if (a > 0) out.push({ text: rest.slice(0, a), bold: false })
    out.push({ text: rest.slice(a + 2, b), bold: true })
    rest = rest.slice(b + 2)
  }
  if (rest) out.push({ text: rest, bold: false })
  return out
}

function tableCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim())
}

const isRule = (line: string) => /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line.trim())

export function parseManualMarkdown(md: string): Block[] {
  const lines = md.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (line.trim() === '') {
      i++
      continue
    }
    const h = /^(#{1,3}) (.+)$/.exec(line)
    if (h) {
      blocks.push({ kind: 'heading', level: h[1].length as 1 | 2 | 3, text: parseInline(h[2].trim()) })
      i++
      continue
    }
    if (line.startsWith('|')) {
      const tbl: string[] = []
      while (i < lines.length && lines[i].startsWith('|')) tbl.push(lines[i++])
      const header = tableCells(tbl[0]).map(parseInline)
      const body = tbl.slice(1).filter((l) => !isRule(l))
      blocks.push({ kind: 'table', header, rows: body.map((l) => tableCells(l).map(parseInline)) })
      continue
    }
    if (line.startsWith('> ')) {
      const parts: string[] = []
      while (i < lines.length && lines[i].startsWith('> ')) parts.push(lines[i++].slice(2))
      blocks.push({ kind: 'callout', text: parseInline(parts.join(' ')) })
      continue
    }
    if (line.startsWith('- ')) {
      const items: Inline[][] = []
      while (i < lines.length && lines[i].startsWith('- ')) items.push(parseInline(lines[i++].slice(2)))
      blocks.push({ kind: 'bullets', items })
      continue
    }
    const num = /^(\d+)\. (.*)$/.exec(line)
    if (num) {
      const items: Inline[][] = []
      const start = Number(num[1])
      while (i < lines.length) {
        const m = /^(\d+)\. (.*)$/.exec(lines[i])
        if (!m) break
        items.push(parseInline(m[2]))
        i++
      }
      blocks.push({ kind: 'numbered', start, items })
      continue
    }
    const para: string[] = []
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(#{1,3} |\||> |- |\d+\. )/.test(lines[i])
    ) {
      para.push(lines[i++])
    }
    blocks.push({ kind: 'paragraph', text: parseInline(para.join(' ')) })
  }
  return blocks
}

/** The Part a section key belongs to: '2.4.leave' and 'SOP 2.5' are both Part 2. */
export function partOfSectionKey(key: string): number | null {
  const m = /^(?:SOP )?(\d+)\./.exec(key)
  if (!m) return null
  const n = Number(m[1])
  return n >= 0 && n <= 8 ? n : null
}

/** The address that opens the Manual page on one section. */
export function sectionHref(key: string): string {
  return `/manual?section=${encodeURIComponent(key)}`
}

/** What "Copy reference" puts on the clipboard: words a person can paste, then the link. */
export function sectionReference(edition: string, heading: string, origin: string, key: string): string {
  return `Fleetly Platform Manual ${edition}, ${heading}: ${origin}${sectionHref(key)}`
}

/** The DOM id a section is rendered under, safe for any key. */
export function sectionAnchor(key: string): string {
  return 'm-' + key.toLowerCase().replace(/[^a-z0-9]+/g, '-')
}
